import { ReadonlyVec3 } from 'gl-matrix';

/** Convert centimeters to millimeters. */
export function cmToMm(cm: number): number {
    return cm * 10;
}

/** Convert millimeters to centimeters. */
export function mmToCm(mm: number): number {
    return mm / 10;
}

/**
 * Returns the sign (+1 or -1) of the dominant axis component of a
 * 3D direction vector. For axis-aligned unit vectors
 * (e.g. [0, 0, -1]), this returns the sign of the non-zero
 * component.
 *
 * Used to convert a dot-product projection (planeCoordinates) back
 * to the patient-space coordinate along the through-plane axis.
 *
 * @throws if the direction vector is zero-length.
 */
export function getDominantAxisSign(direction: ReadonlyVec3): 1 | -1 {
    let maxAbsVal = 0;
    let sign: 1 | -1 = 1;
    for (let i = 0; i < 3; i++) {
        const absVal = Math.abs(direction[i]);
        if (absVal > maxAbsVal) {
            maxAbsVal = absVal;
            sign = direction[i] >= 0 ? 1 : -1;
        }
    }
    if (maxAbsVal === 0) {
        throw new Error('getDominantAxisSign: zero-length direction vector');
    }
    return sign;
}

/**
 * Calculate the normalized orientation value for a plane coordinate.
 * Converts planeCoordinates (mm, projected along view direction) to
 * the patient-space coordinate (cm) for display.
 *
 * @param planeCoordinates - dot(intersectionPoint, viewDirection) in mm
 * @param viewDirectionSign - sign of the dominant axis component of the
 *   view direction vector (+1 or -1). This corrects the projection so
 *   that the displayed coordinate matches the patient axis regardless
 *   of whether the view direction is positive or negative along that axis.
 */
export function calculateNormalizedOrientationValue(
    planeCoordinates: number,
    viewDirectionSign: number = -1
): string {
    const patientCoordMm = planeCoordinates * viewDirectionSign;
    return (Math.round(patientCoordMm * 10) / 100).toFixed(2);
}

/**
 * Formats a value already in centimeters as a display string with
 * fixed decimal places.
 * @param valueCm - The value in centimeters.
 * @param decimalPlaces - Number of decimal places (default: 2).
 * @returns Formatted string, e.g. "1.23".
 */
export function formatCm(valueCm: number, decimalPlaces: number = 2): string {
    return valueCm.toFixed(decimalPlaces);
}

/**
 * Converts an internal mm value to centimeters and formats it as a
 * display string with fixed decimal places.
 *
 * Use 3 decimal places for voxel-scale distances (pixel spacing,
 * slice thickness) and 2 for macro distances (field of view,
 * physical extent). See docs/ui_specs.md for the full convention.
 *
 * @param valueMm - The value in millimeters (domain units).
 * @param decimalPlaces - Number of decimal places (default: 2).
 * @returns Formatted cm string, e.g. "0.098" or "25.60".
 */
export function formatMmAsCm(
    valueMm: number,
    decimalPlaces: number = 2
): string {
    return formatCm(mmToCm(valueMm), decimalPlaces);
}
